import type { WebLegacyWidgetEntity } from '@web/types';
import type { EduLoginButtonEntity, EduLoginButtonItem, LegacyEduLoginButtonZero } from '@sites/entity-core';

import { EButtonHoverType, EButtonStyleType, EButtonWidthType, EHorizontalPosition, ESize } from '@common/enums';

import { themeColorCss } from '@sites/site-theme/src/utils/variables';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';

/**
 * CSS custom properties for the EDU-login button widget — a structural fork of
 * the button widget's `button-view.css-vars`. ONE module shared by both apps;
 * the static rules consuming the variables live in each component's `.less`.
 *
 * A `null` value removes the property from the element, so the consuming
 * declaration falls back to its `var()` default.
 */
export type EduLoginButtonCssVars = Record<string, string | null>;

export const CONTAINER_INNER_MARGIN = {
    [ESize.S]: '-5px',
    [ESize.M]: '-10px',
    [ESize.L]: '-15px',
};

export const BUTTON_OUTER_MARGIN = {
    [ESize.S]: '5px',
    [ESize.M]: '10px',
    [ESize.L]: '15px',
};

/** Variables for one rendered button (the `.sp-button-item` anchor/span). */
export function getButtonItemCssVars(
    widget: WebLegacyWidgetEntity<EduLoginButtonEntity['data']>,
    button: EduLoginButtonItem
): EduLoginButtonCssVars {
    const isZero = widget.type === 'zero';
    const widthType = button.widthType || EButtonWidthType.auto;

    const vars: EduLoginButtonCssVars = {
        '--btn-flex-direction': button.buttonIcon?.position === EHorizontalPosition.L ? 'row' : 'row-reverse',
    };

    if (isZero) {
        vars['--btn-min-width'] = `${(widget.data as LegacyEduLoginButtonZero['data']).position.width}px`;
    } else {
        vars['--btn-width'] = widthType !== EButtonWidthType.auto ? '100%' : 'auto';
        vars['--btn-max-width'] =
            widthType === EButtonWidthType.fixed && button.widthValue != null ? `${button.widthValue}px` : 'auto';
    }

    if (button.isShadow) {
        const s = button.shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
        vars['--btn-shadow'] = `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.spread}px ${s.color}`;
    }

    if (button.styleType === EButtonStyleType.custom) {
        Object.assign(vars, getCustomButtonCssVars(button));
    }

    return vars;
}

/**
 * Modifier classes gating the variable-driven rules — needed because an unset
 * custom property would otherwise reset the declaration to its initial value
 * instead of leaving the themed/global styles intact.
 */
export function getButtonItemCssClasses(button: EduLoginButtonItem): string {
    const classes: string[] = [];

    if (button.isShadow) {
        classes.push('btn-shadow');
    }

    if (button.styleType === EButtonStyleType.custom) {
        classes.push('btn-custom');

        if (button.advancedBorderSettings) {
            classes.push('btn-border-advanced');
        }

        switch (button.buttonHover?.hoverType as EButtonHoverType | undefined) {
            case EButtonHoverType.opacity:
                classes.push('btn-hover-opacity');
                break;
            case EButtonHoverType.shadow:
                classes.push('btn-hover-shadow');
                break;
            case EButtonHoverType.color:
                classes.push('btn-hover-color');
                break;
            default:
                break;
        }
    }

    return classes.join(' ');
}

function getCustomButtonCssVars(button: EduLoginButtonItem): EduLoginButtonCssVars {
    const base = button.buttonBase;
    const borders = button.advancedBorderSettings;
    const radius = button.advancedRadius;
    const hover = button.buttonHover;

    const vars: EduLoginButtonCssVars = {
        '--btn-color': themeColorCss(base?.color?.[0]) || null,
        '--btn-bg': themeColorCss(base?.background?.[0]) || null,
    };

    if (borders) {
        const side = (width: number, style: string, color: string): string => `${width}px ${style} ${color}`;
        const top = side(borders.borderTopWidth, borders.borderTopStyle, borders.borderTopColor);

        vars['--btn-border-top'] = top;
        vars['--btn-border-right'] = borders.isIndependentBorders
            ? side(borders.borderRightWidth, borders.borderRightStyle, borders.borderRightColor)
            : top;
        vars['--btn-border-bottom'] = borders.isIndependentBorders
            ? side(borders.borderBottomWidth, borders.borderBottomStyle, borders.borderBottomColor)
            : top;
        vars['--btn-border-left'] = borders.isIndependentBorders
            ? side(borders.borderLeftWidth, borders.borderLeftStyle, borders.borderLeftColor)
            : top;
    } else {
        vars['--btn-border-color'] = themeColorCss(base?.borderColor?.[0]) || null;
        vars['--btn-border-width'] = `${base?.borderWidth}px`;
        vars['--btn-border-style'] = base?.borderStyle ?? null;
    }

    if (radius) {
        const corner = (value: number | null | undefined): string => `${value ?? base?.borderRadius}px`;
        vars['--btn-radius'] = [
            corner(radius.borderTopLeftRadius),
            corner(radius.borderTopRightRadius),
            corner(radius.borderBottomRightRadius),
            corner(radius.borderBottomLeftRadius),
        ].join(' ');
    } else {
        vars['--btn-radius'] = `${base?.borderRadius}px`;
    }

    switch (hover?.hoverType as EButtonHoverType | undefined) {
        case EButtonHoverType.opacity:
            vars['--btn-hover-opacity'] = `${hover!.opacity * 0.01}`;
            break;
        case EButtonHoverType.shadow:
            vars['--btn-hover-shadow'] = hover!.shadow;
            break;
        case EButtonHoverType.color:
            vars['--btn-hover-color'] = themeColorCss(hover?.color?.[0]) || null;
            vars['--btn-hover-bg'] = themeColorCss(hover?.background?.[0]) || null;
            vars['--btn-hover-border-color'] = themeColorCss(hover?.borderColor?.[0]) || null;
            break;
        default:
            break;
    }

    return vars;
}
